import React, { useState, useRef } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush,
  ReferenceArea,
  ReferenceLine
} from 'recharts';
import { format, parseISO } from 'date-fns';

interface VitalsChartProps {
  data: any[];
  vitalType: string;
  vitalName: string;
  unit: string;
  color: string;
  normalRange?: { min: number; max: number };
  criticalRange?: { min: number; max: number };
}

type TimeRange = 'minutes' | 'hours' | 'days' | 'weeks';

const VitalsChart: React.FC<VitalsChartProps> = ({
  data,
  vitalType,
  vitalName,
  unit,
  color,
  normalRange,
  criticalRange
}) => {
  const [timeRange, setTimeRange] = useState<TimeRange>('hours');
  const [zoomDomain, setZoomDomain] = useState<[number, number] | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<number | null>(null);
  const chartRef = useRef<any>(null);

  /** Coerce a possibly-string numeric field to a number, or null. */
  const toNum = (v: any): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  // Transform data based on time range
  const getChartData = () => {
    if (!data || data.length === 0) return [];

    return data
      .filter(v => v.vital_type === vitalType)
      .sort((a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime())
      .map(v => ({
        timestamp: new Date(v.recorded_at).getTime(),
        time: v.recorded_at,
        // Postgres NUMERIC arrives as a string via node-postgres. Without coercion
        // "Latest" fails its typeof === 'number' check and renders '-', and
        // "Average" concatenates instead of summing and renders NaN.
        value: vitalType === 'blood_pressure' ? null : toNum(v.value),
        systolic: toNum(v.systolic),
        diastolic: toNum(v.diastolic),
        formattedTime: formatTimeByRange(v.recorded_at, timeRange)
      }));
  };

  const formatTimeByRange = (timestamp: string, range: TimeRange): string => {
    const date = parseISO(timestamp);
    switch (range) {
      case 'minutes':
        return format(date, 'HH:mm:ss');
      case 'hours':
        return format(date, 'HH:mm');
      case 'days':
        return format(date, 'MMM d, HH:mm');
      case 'weeks':
        return format(date, 'MMM d');
      default:
        return format(date, 'HH:mm');
    }
  };

  const chartData = getChartData();

  // Custom tooltip with clinical formatting
  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;
    return (
      <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-3">
        <p className="font-semibold text-gray-800 mb-1">
          {format(parseISO(data.time), 'MMM d, yyyy HH:mm:ss')}
        </p>
        {vitalType === 'blood_pressure' ? (
          <>
            <p className="text-sm">
              <span className="text-red-600 font-medium">Systolic: </span>
              {data.systolic} {unit}
            </p>
            <p className="text-sm">
              <span className="text-blue-600 font-medium">Diastolic: </span>
              {data.diastolic} {unit}
            </p>
          </>
        ) : (
          <p className="text-sm">
            <span className="font-medium" style={{ color }}>
              {vitalName}:{' '}
            </span>
            {data.value} {unit}
          </p>
        )}
        {normalRange && (
          <p className="text-xs text-gray-500 mt-1">
            Normal: {normalRange.min}-{normalRange.max} {unit}
          </p>
        )}
      </div>
    );
  };

  // Zoom handlers
  const handleMouseDown = (e: any) => {
    if (e && e.activeLabel) {
      setIsSelecting(true);
      setSelectionStart(e.activeLabel);
    }
  };

  const handleMouseMove = (e: any) => {
    if (isSelecting && selectionStart && e && e.activeLabel) {
      const start = Math.min(selectionStart, e.activeLabel);
      const end = Math.max(selectionStart, e.activeLabel);
      setZoomDomain([start, end]);
    }
  };

  const handleMouseUp = () => {
    setIsSelecting(false);
    setSelectionStart(null);
  };

  const resetZoom = () => {
    setZoomDomain(null);
  };

  // Time range buttons
  const timeRanges: { value: TimeRange; label: string; hours: number }[] = [
    { value: 'minutes', label: 'Last Hour', hours: 1 },
    { value: 'hours', label: 'Last 24h', hours: 24 },
    { value: 'days', label: 'Last Week', hours: 168 },
    { value: 'weeks', label: 'Last Month', hours: 720 }
  ];

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">{vitalName}</h3>
        <div className="flex space-x-2">
          {timeRanges.map(range => (
            <button
              key={range.value}
              onClick={() => setTimeRange(range.value)}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                timeRange === range.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {range.label}
            </button>
          ))}
          {zoomDomain && (
            <button
              onClick={resetZoom}
              className="px-3 py-1 rounded-lg text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200"
            >
              Reset Zoom
            </button>
          )}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          ref={chartRef}
          data={chartData}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          margin={{ top: 10, right: 30, left: 10, bottom: 30 }}
        >
          <defs>
            <linearGradient id={`gradient-${vitalType}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

          <XAxis
            dataKey="formattedTime"
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
            domain={zoomDomain || ['auto', 'auto']}
          />

          <YAxis
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
            label={{ value: unit, angle: -90, position: 'insideLeft', style: { fontSize: '12px' } }}
          />

          <Tooltip content={<CustomTooltip />} />

          <Legend
            wrapperStyle={{ fontSize: '14px' }}
            iconType="line"
          />

          {/* Normal range reference area */}
          {normalRange && (
            <>
              <ReferenceArea
                y1={normalRange.min}
                y2={normalRange.max}
                fill="#10b981"
                fillOpacity={0.1}
                label="Normal Range"
              />
              <ReferenceLine y={normalRange.min} stroke="#10b981" strokeDasharray="5 5" />
              <ReferenceLine y={normalRange.max} stroke="#10b981" strokeDasharray="5 5" />
            </>
          )}

          {/* Critical range reference lines */}
          {criticalRange && (
            <>
              <ReferenceLine y={criticalRange.min} stroke="#ef4444" strokeDasharray="3 3" />
              <ReferenceLine y={criticalRange.max} stroke="#ef4444" strokeDasharray="3 3" />
            </>
          )}

          {/* Blood pressure - dual lines */}
          {vitalType === 'blood_pressure' ? (
            <>
              <Line
                type="monotone"
                dataKey="systolic"
                stroke="#ef4444"
                strokeWidth={2}
                dot={{ r: 3, fill: '#ef4444' }}
                activeDot={{ r: 5 }}
                name="Systolic"
                animationDuration={800}
                animationEasing="ease-in-out"
              />
              <Line
                type="monotone"
                dataKey="diastolic"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ r: 3, fill: '#3b82f6' }}
                activeDot={{ r: 5 }}
                name="Diastolic"
                animationDuration={800}
                animationEasing="ease-in-out"
              />
            </>
          ) : (
            <Line
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2.5}
              dot={{ r: 4, fill: color, strokeWidth: 0 }}
              activeDot={{ r: 6, fill: color }}
              fill={`url(#gradient-${vitalType})`}
              name={vitalName}
              animationDuration={800}
              animationEasing="ease-in-out"
            />
          )}

          {/* Brush for overview/minimap */}
          <Brush
            dataKey="formattedTime"
            height={30}
            stroke={color}
            fill="#f3f4f6"
            travellerWidth={10}
          />

          {/* Selection area for zoom */}
          {isSelecting && selectionStart && zoomDomain && (
            <ReferenceArea
              x1={selectionStart}
              x2={zoomDomain[1]}
              strokeOpacity={0.3}
              fill="#3b82f6"
              fillOpacity={0.3}
            />
          )}
        </LineChart>
      </ResponsiveContainer>

      {/* Stats summary */}
      <div className="mt-4 grid grid-cols-4 gap-4 text-center">
        {chartData.length > 0 && (() => {
          if (vitalType === 'blood_pressure') {
            const systolicValues = chartData.map(d => d.systolic).filter((v): v is number => v != null);
            const diastolicValues = chartData.map(d => d.diastolic).filter((v): v is number => v != null);
            return (
              <>
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-xs text-gray-600">Latest</p>
                  <p className="text-lg font-semibold" style={{ color }}>
                    {chartData[chartData.length - 1]?.systolic}/{chartData[chartData.length - 1]?.diastolic}
                  </p>
                </div>
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-xs text-gray-600">Average</p>
                  <p className="text-lg font-semibold text-gray-700">
                    {Math.round(systolicValues.reduce((sum, v) => sum + v, 0) / systolicValues.length)}/
                    {Math.round(diastolicValues.reduce((sum, v) => sum + v, 0) / diastolicValues.length)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-xs text-gray-600">Min</p>
                  <p className="text-lg font-semibold text-blue-600">
                    {Math.min(...diastolicValues)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-xs text-gray-600">Max</p>
                  <p className="text-lg font-semibold text-red-600">
                    {Math.max(...systolicValues)}
                  </p>
                </div>
              </>
            );
          } else {
            const values = chartData.map(d => d.value).filter((v): v is number => v != null && !isNaN(v));
            if (values.length === 0) return <div className="col-span-4 text-gray-500">No data available</div>;

            return (
              <>
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-xs text-gray-600">Latest</p>
                  <p className="text-lg font-semibold" style={{ color }}>
                    {chartData[chartData.length - 1]?.value != null
                      ? chartData[chartData.length - 1].value!.toFixed(1)
                      : '-'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-xs text-gray-600">Average</p>
                  <p className="text-lg font-semibold text-gray-700">
                    {(values.reduce((sum, v) => sum + v, 0) / values.length).toFixed(1)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-xs text-gray-600">Min</p>
                  <p className="text-lg font-semibold text-blue-600">
                    {Math.min(...values).toFixed(1)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-xs text-gray-600">Max</p>
                  <p className="text-lg font-semibold text-red-600">
                    {Math.max(...values).toFixed(1)}
                  </p>
                </div>
              </>
            );
          }
        })()}
      </div>
    </div>
  );
};

export default VitalsChart;
